import { useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { App, AppStatus, AuthenticationError, AuthorizationError, CustomDomainStatus, DeploymentMemoryExceededError, DeploymentLog, DockerfileNotFoundError, NetworkError, Subscription, User, DeploymentStatus, KanikoImageBuildError, ContainerLog, ContainerStats, GitHubResourceNotFoundError, GitRepositoryEmptyError, CreditCardValidationRequiredError, TaskStatus } from '@back4app2/sdk';
import GitHubReconnectModal from '../components/GitHubReconnectModal';
import MyAppDeploymentStatus from '../components/MyAppDeploymentStatus';
import Timer from '../components/Timer';
import CancelBuildButton, { isBuildCancelable } from '../components/CancelBuildButton';
import { timeFromNow } from '../utils/dates';
import back4app2 from '../back4app2';
import { BACK4APP_DOT_COM_SITE_URL } from '../settings';
import LogBox from '../components/LogBox';
import { hasFinishedBuilding } from '../utils/deploymentBuild';
import Guides from '../components/Guides';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';
import DockerfileErrorMessage from '../components/CustomErrorMessageUI/DockerfileErrorMessage';
import VerifyEmailErrorMessage from '../components/CustomErrorMessageUI/VerifyEmailErrorMessage';
import SecurityBlockErrorMessage from '../components/CustomErrorMessageUI/SecurityBlockErrorMessage';
import PendingSetupMessage from '../components/CustomErrorMessageUI/PendingSetupMessage';
import OutOfMemoryError from '../components/CustomErrorMessageUI/OutOfMemoryError';
import PaymentPendingErrorMessage from '../components/CustomErrorMessageUI/PaymentPendingErrorMessage';
import ImageBuildError from '../components/CustomErrorMessageUI/ImageBuildError';
import { getContainerStatsGraphData } from '../utils/metrics';
import ContainerLogBox from '../components/ContainerLogBox';
import TemporaryUrlNotice from '../components/TemporaryUrlNotice';
import PaymentPastDueNotice from '../components/PaymentPastDueNotice';
import type { SettingsFocus } from '../utils/settingsFocus';
import toast from 'react-hot-toast';
import { TOAST_SURFACE } from '../components/ds/toast';
import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';
import { ReactComponent as StatusSuccessSVG } from '../assets/images/status-success.svg';
import { DsButton, Eyebrow, MetricChart, ScreenHead, SegmentedControl, StatCard, StatusDot, SurfaceCard, TileRow } from '../components/ds';
import { BoxIcon, BranchIcon, ChevronRightIcon, ExternalLinkIcon, FileIcon, FolderIcon, GlobeIcon, HeartbeatIcon, LinkIcon, PortIcon, TerminalIcon, UploadIcon, VariablesIcon } from '../components/ds/icons';
import { getAppStatusSummary } from '../utils/appStatus';
import { appSourceKind, isBuiltFromSource } from '../utils/appSource';
import InstantRollbackButton from '../components/Rollback/InstantRollbackButton';
import { MyAppSettingsTab } from './MyAppSettings';
import useUpgrade from '../hooks/useUpgrade';
import ResourceNotAccessibleError from '../components/CustomErrorMessageUI/ResourceNotAccessibleError';
import CreditCardValidationRequiredErrorMessage from '../components/CustomErrorMessageUI/CreditCardValidationRequiredErrorMessage';

interface MyAppOverviewState {
  isLoadingDeploymentLogs: boolean;
  deploymentLogsLoadingErrorMessage?: string;
  deploymentLogs?: DeploymentLog[];
  isLoadingStats: boolean;
  loadingStatsErrorMessage?: string;
  colors?: { [containerId: string]: string };
  cpuGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }};
  ramGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }, max: number};
  isLoadingContainerLogs: boolean;
  containerLogsLoadingErrorMessage?: string;
  containerLogs?: ContainerLog[];
}

const INITIAL_STATE: MyAppOverviewState = {
  isLoadingDeploymentLogs: true,
  isLoadingStats: true,
  isLoadingContainerLogs: true
};

enum MyAppOverviewActionType {
  RESET,
  FINISH_LOADING_DEPLOYMENT_LOGS,
  FINISH_LOADING_STATS,
  FINISH_LOADING_CONTAINER_LOGS,
  RESET_DEPLOYMENT_LOGS,
  RESET_CONTAINER_STATS,
  RESET_CONTAINER_LOGS,
}

const reset = () => ({
  type: MyAppOverviewActionType.RESET
} as const);

const resetDeploymentLogs = () => ({
  type: MyAppOverviewActionType.RESET_DEPLOYMENT_LOGS
} as const);
const resetContainerLogs = () => ({
  type: MyAppOverviewActionType.RESET_CONTAINER_LOGS
} as const);
const resetContainerStats = () => ({
  type: MyAppOverviewActionType.RESET_CONTAINER_STATS
} as const);

const finishLoadingDeploymentLogs = (errorMessage?: string, deploymentLogs?: DeploymentLog[]) => ({
  type: MyAppOverviewActionType.FINISH_LOADING_DEPLOYMENT_LOGS,
  payload: {
    errorMessage,
    deploymentLogs
  }
} as const);

const finishLoadingStats = (errorMessage?: string, stats?: ContainerStats[]) => ({
  type: MyAppOverviewActionType.FINISH_LOADING_STATS,
  payload: {
    errorMessage,
    stats
  }
} as const);

const finishLoadingContainerLogs = (errorMessage?: string, logs?: ContainerLog[]) => ({
  type: MyAppOverviewActionType.FINISH_LOADING_CONTAINER_LOGS,
  payload: {
    errorMessage,
    logs
  }
} as const);

const containerStatsTimeWindow = 1 * 60 * 60 * 1000;

type MyAppOverviewAction = ReturnType<typeof reset> | ReturnType<typeof finishLoadingDeploymentLogs> | ReturnType<typeof finishLoadingContainerLogs>  | ReturnType<typeof finishLoadingStats>  | ReturnType<typeof resetDeploymentLogs>  | ReturnType<typeof resetContainerLogs>  | ReturnType<typeof resetContainerStats>;

const reducer = (state: MyAppOverviewState = INITIAL_STATE, action: MyAppOverviewAction): MyAppOverviewState => {
  switch (action.type) {
    case MyAppOverviewActionType.RESET: 
      return INITIAL_STATE;

    case MyAppOverviewActionType.RESET_DEPLOYMENT_LOGS:
      return {
        ...state,
        isLoadingDeploymentLogs: true,
        deploymentLogsLoadingErrorMessage: undefined,
        deploymentLogs: undefined

      }
    case MyAppOverviewActionType.RESET_CONTAINER_LOGS:
      return {
        ...state,
        isLoadingContainerLogs: true,
        containerLogsLoadingErrorMessage: undefined,
        containerLogs: undefined
      }

    case MyAppOverviewActionType.RESET_CONTAINER_STATS:
      return {
        ...state,
        isLoadingStats: true,
        loadingStatsErrorMessage: undefined,
        colors: undefined,
        cpuGraphProps: undefined,
        ramGraphProps: undefined
      }

    case MyAppOverviewActionType.FINISH_LOADING_DEPLOYMENT_LOGS:
      return {
        ...state,
        isLoadingDeploymentLogs: false,
        deploymentLogsLoadingErrorMessage: action.payload.errorMessage,
        deploymentLogs: action.payload.deploymentLogs
      }
    case MyAppOverviewActionType.FINISH_LOADING_STATS: {
      // A reload that failed, over a chart that is already drawn: keep the
      // chart. The subscription reloads on its own — on the next sample, on
      // reconnect — so the failure is over within seconds, and swapping the
      // plot for an error and back again was the only sign it ever happened.
      // The error is for when there is nothing to show instead.
      if (action.payload.errorMessage && state.cpuGraphProps && !state.loadingStatsErrorMessage) {
        return { ...state, isLoadingStats: false };
      }

      const { colors,
        cpuGraphProps,
        ramGraphProps,
      } = getContainerStatsGraphData(action.payload.stats || [], state.colors)
      
      return {
        ...state,
        isLoadingStats: false,
        loadingStatsErrorMessage: action.payload.errorMessage,
        colors,
        cpuGraphProps,
        ramGraphProps
      };
    }
    case MyAppOverviewActionType.FINISH_LOADING_CONTAINER_LOGS:
      return {
        ...state,
        isLoadingContainerLogs: false,
        containerLogsLoadingErrorMessage: action.payload.errorMessage,
        containerLogs: action.payload.logs
      };
  }
};

// The CPU and RAM cards on the Overview are a glance, not the Metrics screen:
// flat enough that the plan card beside them is not stretched into a tall row
// with most of it empty. The three Y ticks still clear each other at this size.
const OVERVIEW_PLOT_HEIGHT = 80;

// The cell that squares off the configuration grid's last row at four across,
// by how many rows are left over: one over leaves three cells to fill.
const CONFIG_FILL = ['', 'xl:block xl:col-span-3', 'xl:block xl:col-span-2', 'xl:block xl:col-span-1'];

const MyAppOverview = () => {
  const { app, appLoadedAt, user, goToSettingsTab } = useOutletContext<{
    app: App,
    /** When the shell fetched the app — the Timer counts up from it. */
    appLoadedAt: Date,
    user: User,
    /** The shell owns it: it asks before dropping unsaved edits. */
    goToSettingsTab: (id: string, focus?: SettingsFocus) => void,
  }>();
  const mainServiceEnvironment = app && app.mainService && app.mainService.mainServiceEnvironment;
  const mainServiceEnvironmentId = mainServiceEnvironment && mainServiceEnvironment.id;
  const lastDeployment = app && app.mainService && app.mainService.mainServiceEnvironment && app.mainService.mainServiceEnvironment.lastDeployment;
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  // WHICH failure was waved away, not merely that one was. A page-session flag
  // silenced every later failure too: dismiss it once — and any click outside
  // the modal counts as dismissing — and the next failed deploy offered
  // nothing, on a screen that had just updated itself to show that failure.
  // Reloading was the only way back to the offer.
  const [githubReconnectDismissedFor, setGithubReconnectDismissedFor] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const upgrade = useUpgrade(app.id, { source: 'overview_plan_card' });

  // The code, not the log text. The worker writes GITHUB_INSTALLATION_INVALID
  // on the deployment precisely so a screen can branch on it — matching the
  // sentence in the log panel would break the day someone rewords it.
  const needsGithubReconnect =
    lastDeployment?.status === DeploymentStatus.FAILED &&
    lastDeployment?.errorCode === 'GITHUB_INSTALLATION_INVALID';
  const { deploymentLogsLoadingErrorMessage, deploymentLogs, isLoadingDeploymentLogs, containerLogsLoadingErrorMessage, containerLogs, isLoadingContainerLogs, isLoadingStats, loadingStatsErrorMessage, cpuGraphProps, ramGraphProps } = state;

  // Build log and runtime log used to be two nearly identical panels stacked
  // on the same page. One panel, two tabs — nothing is unreachable, and the
  // log gets the width it always needed.
  const [logTab, setLogTab] = useState<'build' | 'runtime'>('build');
  /**
   * How many domains answer for this app.
   *
   * `mainCustomDomain` comes with the app, but it is one of possibly several
   * and the summary counts them. A number is all this screen wants, so it
   * stays in local state rather than going through the reducer — and a failure
   * leaves it `undefined`, which the row reads as "the main one, and no count".
   */
  const [domainCount, setDomainCount] = useState<number | undefined>(undefined);

  const status = getAppStatusSummary(app);

  const plan = mainServiceEnvironment?.plan;
  // A cancelled plan is not a former plan: it runs, and it was paid for, until
  // this date. The card would otherwise show the paid tier with no hint that it
  // has an end — and this is the screen people actually look at.
  const planCancelsAt = mainServiceEnvironment?.planCancelsAt
    ? new Date(mainServiceEnvironment.planCancelsAt)
    : undefined;
  const planEndsOn = planCancelsAt && planCancelsAt.getTime() > Date.now()
    ? planCancelsAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : undefined;
  const envVarCount = mainServiceEnvironment?.envVars?.length ?? 0;
  /**
   * The address the app is served on, or nothing.
   *
   * The same three conditions the sidebar's link is written under: there has
   * to be an environment, its main domain has to still answer — an EXPIRED one
   * has had its balancer rules pulled — and a blocked app must not advertise a
   * live address anywhere.
   */
  const appUrl = mainServiceEnvironment?.mainCustomDomain
    && mainServiceEnvironment.mainCustomDomain.status !== CustomDomainStatus.EXPIRED
    && !app.block
    ? mainServiceEnvironment.mainCustomDomain.name
    : undefined;
  const planRAM = plan?.maxRAM
    ? (plan.maxRAM >= 1024 ? `${plan.maxRAM / 1024}GB` : `${plan.maxRAM}MB`)
    : undefined;
  // The RAM chart plots raw bytes, and `maxRAM` is written in MB — the two
  // have to meet somewhere for the axis and the label to name one number.
  const ramLimitBytes = plan?.maxRAM ? plan.maxRAM * 1024 * 1024 : undefined;


  useEffect(
    () => {
      let subscription: Subscription;

      if (lastDeployment?.id) {
        subscription = back4app2.subscribeToDeploymentLogs(
          lastDeployment.id,
          (error, snapshot) => {
            if (error) {
              if (error instanceof NetworkError) {
                console.error('network error', error);
                dispatch(finishLoadingDeploymentLogs('Network error when loading last deployment logs. Check your internet connection and try again.'));
              } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
                window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
              } else {
                console.error('unexpected error loading last deployment logs', error);
                dispatch(finishLoadingDeploymentLogs('Unexpected error when loading last deployment logs. Please try again.'));
              }
            } else {
              dispatch(finishLoadingDeploymentLogs(undefined, snapshot));
            }
          }
        );
      }

      return () => {
        if (subscription) {
          subscription.unsubscribe();
          dispatch(resetDeploymentLogs());
        }
      };
    },
    [
      lastDeployment?.id
    ]
  );

  useEffect(() => {
    let statsSubscription: Subscription;
    if (mainServiceEnvironmentId) {
      statsSubscription = back4app2.subscribeToContainersStats(
        mainServiceEnvironmentId,
        containerStatsTimeWindow,
        (error, snapshot) => {
          if (error) {
            if (error instanceof NetworkError) {
              console.error('network error', error);
              dispatch(finishLoadingStats('Network error when loading app stats. Check your internet connection and try again.'));
            } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
              window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
            } else {
              console.error('unexpected error loading app stats', error);
              dispatch(finishLoadingStats('Unexpected error when loading app stats. Please try again.'));
            }
          } else {
            dispatch(finishLoadingStats(undefined, snapshot));
          }
        }
      )
    }
  
    return () => {
      if (statsSubscription) {
        statsSubscription.unsubscribe();          
        dispatch(resetContainerStats());
      }
    }
  }, [mainServiceEnvironmentId]);

  useEffect(
    () => {
      let subscription: Subscription;

      if (mainServiceEnvironmentId) {
        subscription = back4app2.subscribeToContainersLogs(
          mainServiceEnvironmentId,
          (error, snapshot) => {
            if (error) {
              if (error instanceof NetworkError) {
                console.error('network error', error);
                dispatch(finishLoadingContainerLogs('Network error when loading app logs. Check your internet connection and try again.'));
              } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
                window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
              } else {
                console.error('unexpected error loading app logs', error);
                dispatch(finishLoadingContainerLogs('Unexpected error when loading app logs. Please try again.'));
              }
            } else {
              dispatch(finishLoadingContainerLogs(undefined, snapshot));
            }
          }
        );
      }

      return () => {
        if (subscription) {
          subscription.unsubscribe();
          dispatch(resetContainerLogs());
        }
      };
    },
    [
      mainServiceEnvironmentId
    ]
  );

  useEffect(() => {
    let subscription: Subscription;

    if (mainServiceEnvironmentId) {
      subscription = back4app2.subscribeToCustomDomains(mainServiceEnvironmentId, (error, snapshot) => {
        if (error) {
          // A count nobody asked for is not worth a banner, and the row reads
          // without it. Sign-in is the exception, as everywhere else here.
          if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
            window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
          } else {
            console.error('unexpected error loading custom domains', error);
          }
        } else {
          setDomainCount(snapshot?.length);
        }
      });
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
      setDomainCount(undefined);
    };
  }, [mainServiceEnvironmentId]);

  /**
   * A build in flight, and the two things this screen owes whoever is
   * watching it.
   *
   * The log first. Its newest line is its last, at the bottom of a card that
   * starts halfway down the page — on a laptop that put the end of the build
   * under the fold, and people sat reading the middle of a log that had
   * already finished below them. So when a build starts, or the screen opens
   * on one, the card is brought wholly into view: once per deployment, and
   * `nearest`, which does nothing at all when it is in view already.
   *
   * Then the end, said out loud. "Ready" appears in the status line and in the
   * sidebar, and neither moves; a toast does. Only for a build that was seen
   * running here — opening the page on a deployment from last week announces
   * nothing — and not for one the customer canceled, who knows.
   */
  const logCardRef = useRef<HTMLDivElement>(null);
  const watchedDeploymentId = useRef<string | undefined>(undefined);
  const lastDeploymentId = lastDeployment?.id;
  const lastDeploymentStatus = lastDeployment?.status;
  const lastDeployTaskStatus = lastDeployment?.deployTask?.status;
  const isBuilding = !!lastDeployment && !hasFinishedBuilding(lastDeployment);
  // The first deploy has its own announcement, and a toast under a dialog
  // saying the same thing is one of them too many.
  const hasFirstDeployDialog = app.isOwner && app.firstDeployCelebrated === false;

  useEffect(() => {
    if (!lastDeploymentId || !isBuilding) {
      return;
    }

    // After the frame: the cards above are still taking their height.
    const frame = window.requestAnimationFrame(() => {
      logCardRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [lastDeploymentId, isBuilding]);

  useEffect(() => {
    if (!lastDeploymentId) {
      return;
    }

    if (isBuilding) {
      watchedDeploymentId.current = lastDeploymentId;

      return;
    }

    if (watchedDeploymentId.current !== lastDeploymentId) {
      return;
    }

    const failed = lastDeploymentStatus === DeploymentStatus.FAILED || lastDeployTaskStatus === TaskStatus.FAILED;
    const canceled = lastDeploymentStatus === DeploymentStatus.CANCELED;
    const live = lastDeploymentStatus === DeploymentStatus.READY;

    // The task can end a moment before the deployment says how.
    if (!failed && !canceled && !live) {
      return;
    }

    watchedDeploymentId.current = undefined;

    if (failed) {
      toast.error('Deploy failed. The deployment log has the reason.', {
        className: TOAST_SURFACE,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000,
      });
    } else if (live && !hasFirstDeployDialog) {
      toast.success('Deploy finished. Your app is live.', {
        className: TOAST_SURFACE,
        icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000,
      });
    }
  }, [lastDeploymentId, isBuilding, lastDeploymentStatus, lastDeployTaskStatus, hasFirstDeployDialog]);

  useEffect(() => {
    trackEvent(AmplitudeEvent.AT_APP_OVERVIEW_PAGE);
    return () => {
      dispatch(reset())
    }
  }, []);

  const planAllowances = plan
    ? [
      `${plan.maxCPU} vCPU`,
      planRAM,
      typeof plan.transferLimit === 'number' ? `${plan.transferLimit} GB transfer` : undefined,
    ].filter(Boolean).join(' · ')
    : '';
  const planEnds = planEndsOn ? (
    <span className="ml-2 font-courrier text-ds-105 uppercase tracking-[0.08em] text-[#F5B544]">
      ends {planEndsOn}
    </span>
  ) : null;
  const isFreePlan = !plan || plan.name.includes('Free');
  const planButton = (
    <button
      type="button"
      onClick={upgrade}
      className="b4-focus flex-none rounded-control text-ds-125 lg:text-ds-135 font-semibold text-action-soft hover:text-white"
    >
      {isFreePlan ? 'Upgrade' : 'Manage'}
    </button>
  );

  // From `lg` up. A phone has its own Upgrade button, so there a Free plan
  // has no card, and a paid one has the flat card below.
  const planCard = (
    <StatCard
      label="plan"
      value={plan?.name ?? '—'}
      unit={plan?.price ? `$${plan.price}.00 / mo` : undefined}
      tone={plan ? 'value' : 'empty'}
      className="hidden lg:flex lg:col-span-2 xl:col-span-1"
      note={plan ? <>{planAllowances}{planEnds}</> : 'no plan on this app yet'}
      action={planButton}
    />
  );

  // The phone's card for a paid plan: two lines, the tier and its price over
  // the allowances, with Manage beside them.
  const phonePlanCard = plan && !isFreePlan ? (
    <SurfaceCard className="lg:hidden px-4 py-3 flex items-center justify-between gap-3">
      <div className="min-w-0 flex flex-col gap-0.5">
        <span className="flex items-baseline gap-2 min-w-0">
          <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white truncate">{plan.name}</span>
          {plan.price ? <span className="flex-none text-ds-125 text-navy-300">${plan.price}.00 / mo</span> : null}
        </span>
        <span className="font-courrier text-ds-115 text-navy-200 truncate">{planAllowances}{planEnds}</span>
      </div>
      {planButton}
    </SurfaceCard>
  ) : null;

  // The plan is not paid for, or is a Free one this account may no longer
  // have. The screen says so in place of the log and the configuration.
  const isPaymentPending = mainServiceEnvironment?.isPendingPayment
    || (!mainServiceEnvironment?.isFreePlanElegible && mainServiceEnvironment?.plan.name.includes('Free'));
  // The log is drawn for an app that has got as far as deploying: not one
  // still waiting on its setup, its owner's email or a payment, each of which
  // puts a notice where the log would be.
  const showsLog = !(app.pendingFirstBuild && mainServiceEnvironment)
    && app.status !== AppStatus.PENDING_VERIFICATION
    && !isPaymentPending;

  // The phone's own log card. It was a door for a while — one row that opened
  // the Logs screen — and the two things wrong with that were that the
  // deployment log is not on that screen at all, and that reading a deploy is
  // the reason you opened the app on a phone in the first place.
  //
  // So: the same two tabs, the same wells, and nothing else. No deployment
  // meta line — the tab says which log you are reading, and a deploy's own
  // facts (id, commit, branch) are what the Deployments screen is. The well is
  // shorter than the desktop one and the expand button inside it goes full
  // screen. The desktop card, from `lg` up, is its own and sits under the
  // charts.
  const phoneLogCard = (
    <SurfaceCard tone="surface" className="lg:hidden flex flex-col overflow-hidden">
      <div className="flex-none px-3 py-2.5 border-b border-navy-700 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        {/* `touch`, because here the tabs are a thumb's target and
            not a pointer's. */}
        <SegmentedControl
          ariaLabel="Log source"
          size="touch"
          value={logTab}
          onChange={setLogTab}
          segments={[
            { value: 'build', label: 'Deployment log' },
            { value: 'runtime', label: 'Runtime log' },
          ]}
        />
        {/* The words the bottom bar uses, because that is where
            these two go. */}
        <button
          type="button"
          onClick={() => navigate(`/apps/${app.id}/${logTab === 'build' ? 'deployments' : 'logs'}`)}
          className="b4-focus flex-none min-h-[32px] px-1 rounded-control text-ds-125 font-semibold text-action-soft"
        >
          {logTab === 'build' ? 'All deploys' : 'All logs'}
        </button>
      </div>
      {/* 17rem of log: enough to read the tail of a build without
          the card owning the screen the way the 26rem desktop well
          would on a phone. */}
      <div className="grow shrink basis-[17rem] min-h-0">
        {logTab === 'build' ? (
          lastDeployment ? (
            <LogBox
              errorMessage={deploymentLogsLoadingErrorMessage}
              logs={deploymentLogs}
              isLoading={isLoadingDeploymentLogs}
              canMaximize
              fullHeight
              hasFinished={hasFinishedBuilding(lastDeployment)}
            />
          ) : (
            <div className="h-full bg-navy-950 flex items-center justify-center text-ds-135 text-navy-300">
              Nothing built yet.
            </div>
          )
        ) : (
          mainServiceEnvironmentId ? (
            <ContainerLogBox
              errorMessage={containerLogsLoadingErrorMessage}
              logs={containerLogs}
              isLoading={isLoadingContainerLogs}
              canMaximize
              fullHeight
            />
          ) : (
            <div className="h-full bg-navy-950 flex items-center justify-center text-ds-135 text-navy-300">
              No container running.
            </div>
          )
        )}
      </div>
    </SurfaceCard>
  );

  // What this app is set to, and the way to each screen that sets it. It
  // began as the publish drawer's "what will happen" — source, port,
  // visibility — which answered for the deploy and stopped there; the
  // questions people came to the Overview with ("how many variables does it
  // have?", "is the health check on?") were a click into Settings away with no
  // hint of the answer.
  //
  // Every row is a button into its own section, so the summary is also the
  // shortest route to changing what it reports. One list, drawn twice: a
  // column of rows on a phone and a grid of cells from `lg` up.
  const sourceKind = mainServiceEnvironment
    ? appSourceKind(mainServiceEnvironment, !!app.mainService?.repository)
    : undefined;
  const configRows: { Icon: typeof FileIcon, eyebrow: string, value: string, onClick: () => void }[] = mainServiceEnvironment && sourceKind ? [
    {
      Icon: FileIcon,
      eyebrow: 'source',
      value: {
        GITHUB: `GitHub · ${mainServiceEnvironment.branchName}${mainServiceEnvironment.autoDeploy ? ' · auto' : ''}`,
        DOCKER_HUB: `Image · ${mainServiceEnvironment.imageRepository || 'image'}:${mainServiceEnvironment.imageTag || 'latest'}`,
        // No repository and no push: each new upload is a deploy.
        UPLOAD: 'Upload · files from your computer',
      }[sourceKind],
      onClick: () => goToSettingsTab(MyAppSettingsTab.BUILD_AND_DEPLOY, 'source'),
    },
    // Build method and root directory used to share one row, joined by a dot —
    // two unrelated answers reading as one value, and the folder path (the
    // longer of the two) was the half that got truncated away. They are
    // separate questions and get a row each.
    //
    // Neither is asked of an app running a published image: it is not built
    // here, so there is no Dockerfile to choose over buildpacks and no folder
    // to build from.
    ...(isBuiltFromSource(sourceKind) ? [
      {
        Icon: BoxIcon,
        eyebrow: 'build',
        value: mainServiceEnvironment.isStaticSite
          ? 'Static site'
          : mainServiceEnvironment.builderPreference === 'BUILDPACK' ? 'Buildpack' : 'Dockerfile',
        onClick: () => goToSettingsTab(MyAppSettingsTab.BUILD_AND_DEPLOY, 'build'),
      },
      {
        Icon: FolderIcon,
        eyebrow: 'root directory',
        // The root is written four ways depending on when the app was made —
        // '', '/', '.' and './' all mean it.
        value: ['', '/', './', '.'].includes(mainServiceEnvironment.rootDirPath || '')
          ? 'repository root'
          : mainServiceEnvironment.rootDirPath || '',
        onClick: () => goToSettingsTab(MyAppSettingsTab.BUILD_AND_DEPLOY, 'root-directory'),
      },
    ] : []),
    // The port and the command used to share a row behind a dot. A start
    // command is the longest string on this card — `gunicorn app:app --bind
    // 0.0.0.0:8000` — so the half that got truncated was always the half with
    // something to say, and the port it was truncating for is four characters.
    {
      Icon: PortIcon,
      eyebrow: 'port',
      value: mainServiceEnvironment.exposedPort
        ? `${mainServiceEnvironment.exposedPort}/tcp`
        : 'auto',
      onClick: () => goToSettingsTab(MyAppSettingsTab.BUILD_AND_DEPLOY, 'port'),
    },
    {
      Icon: TerminalIcon,
      eyebrow: 'start command',
      // Empty is not "unset": it is the documented default, and the settings
      // field says so in its own words.
      value: mainServiceEnvironment.startCommand || 'Procfile or start script',
      onClick: () => goToSettingsTab(MyAppSettingsTab.BUILD_AND_DEPLOY, 'start-command'),
    },
    {
      Icon: VariablesIcon,
      eyebrow: 'env variables',
      value: envVarCount === 0
        ? 'none set'
        : `${envVarCount} ${envVarCount === 1 ? 'variable' : 'variables'}`,
      onClick: () => goToSettingsTab(MyAppSettingsTab.VARIABLES),
    },
    {
      Icon: GlobeIcon,
      eyebrow: 'domains',
      // How many, not which one. A domain name is the one value on this card
      // with no useful truncation — the tail is where it differs — and the row
      // was carrying the main one plus a `+2` for the rest, which is a count
      // written twice. Every row here is a door; this one now says how much is
      // behind it, like the variables row above it.
      value: app.block
        ? 'hidden while under review'
        : domainCount === undefined
          ? (mainServiceEnvironment.mainCustomDomain?.name || 'no URL yet')
          : domainCount === 0
            ? 'no domains yet'
            : `${domainCount} ${domainCount === 1 ? 'domain' : 'domains'}`,
      onClick: () => goToSettingsTab(MyAppSettingsTab.CUSTOM_DOMAIN),
    },
    {
      Icon: HeartbeatIcon,
      eyebrow: 'health check',
      value: mainServiceEnvironment.healthCheckEndpoint || 'not set',
      onClick: () => goToSettingsTab(MyAppSettingsTab.HEALTH_AND_ALERT, 'health-check'),
    },
  ] : [];

  // Blocked by the security review: the card that says so, and nothing else,
  // not even the plan. The readings, the log and the configuration
  // are of an app that is not running, and a board of zeros around the one
  // card that matters made it one panel among many.
  if (app.block) {
    return (<>
      <div className="hidden lg:flex items-start justify-between gap-3">
        <ScreenHead title="Overview">
          <span className="flex items-center gap-2 min-w-0">
            <StatusDot tone="error" />
            <span className="text-ds-135 text-navy-100 truncate">{status.text}</span>
          </span>
        </ScreenHead>
      </div>
      <SecurityBlockErrorMessage
        appId={app.id}
        block={app.block}
        userEmail={user.username}
      />
    </>);
  }

  return (<>
    {/* Opens itself, once per failure, on the one it can fix. Dismissing
        records THAT deployment's id, so the modal does not come back on every
        re-render of a page that stays open — the failed deployment is still
        the last one, and without this the customer could not read the log
        underneath it. A later deploy that fails the same way is a different
        deployment, so it is offered again, on the open page rather than only
        after a reload. */}
    <GitHubReconnectModal
      open={needsGithubReconnect && githubReconnectDismissedFor !== lastDeployment?.id}
      onClose={() => setGithubReconnectDismissedFor(lastDeployment?.id)}
      serviceEnvironmentId={mainServiceEnvironmentId}
      repositoryId={app?.mainService?.repository?.id}
      repositoryFullName={app?.mainService?.repository?.fullName}
    />

    {/* The name of the screen, when it was last deployed, and the two things
        to do from here: open what is running, and put back what ran before.

        The state is not written here any more. It was — a dot and a word
        beside the title — while the sidebar said the same word a hand's width
        to the left; the sidebar keeps it, as the one reading of it every
        screen has, and its rail keeps the dot.

        The address is a button here and a line of type in the sidebar. It is
        the thing most people open this screen to click, and the rail has no
        room for it at all.

        The margin under the head is on the row, not on `ScreenHead`: the
        buttons are taller than the title, and centred against a box that
        carries its own space below, the title sat high. */}
    {/* Desktop only: on a phone the app header carries the state and the age,
        the address is the first row of the configuration, and the rollback is
        on the Deployments list. */}
    <div className="hidden lg:flex items-center justify-between gap-4 mb-4">
      <ScreenHead title="Overview" className="lg:!mb-0">
        {lastDeployment?.createdAt ? (
          <span className="font-courrier text-ds-115 text-navy-300 truncate">
            deployed {timeFromNow(lastDeployment.createdAt)}
          </span>
        ) : null}
      </ScreenHead>
      <div className="flex-none flex items-center gap-4">
        {appUrl ? (
          <DsButton variant="outline-ok" size="lg" href={`${window.location.protocol}//${appUrl}`} title={appUrl}>
            Open live app
            <ExternalLinkIcon size={14} />
          </DsButton>
        ) : null}
        {app ? <InstantRollbackButton app={app} /> : null}
      </div>
    </div>

    {/* One column under the head, with more air between its sections than the
        shell puts between two cards: these are the four parts of the screen —
        readings, log, configuration, guides — and at a card's gap they read
        as one long list. The head stays outside it, with its own space. */}
    <div className="flex flex-col gap-5 lg:gap-10 [&>*]:min-w-0">
      {/* The phone's copy of the sidebar's notice. The sidebar that carries it
          is `hidden lg:flex`, and the header strip below it has room for the
          address and nothing after it — so on a phone this screen is the only
          place a free-plan customer can be told their URL is rented. */}
      <div className="lg:hidden flex flex-col gap-3">
        <TemporaryUrlNotice app={app} />
        <PaymentPastDueNotice app={app} onUpdated={() => window.location.reload()} />
      </div>

      {/* The plan, then the two readings it is the ceiling for — drawn against
          that ceiling rather than reported as a bare number.

          A single number said what the app is using and left "of how much?" to
          a note underneath, which is the half of the question a customer opens
          this screen with. The chart answers both at once: the plot is the use,
          the top of the Y axis is the limit, and the distance between them is
          the headroom. It is the reading the Overview drew before the restyle,
          in the shape the Metrics screen now draws it.

          Three equal columns from `xl` up. Below that the two charts keep a
          row to themselves and the plan runs the width of it above them: at a
          third of a laptop's page a plot is too narrow to read, and the plan's
          three short lines are the ones that can give up the room.

          One column on a phone rather than two: a plot 170px wide is a smear,
          and these are the two cards on the screen that are pictures. */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-6 xl:grid-cols-3">
        {/* The plan sits with the readings it is the ceiling for: the CPU and
            RAM charts beside it are plotted against these numbers, and they used
            to be a column apart. It is written as a stat too — eyebrow, value,
            unit, note — because a card of nothing but 12px label/value pairs
            read as a footnote between two headlines. The tier is the value here;
            the allowances are the note, and their units name them.

            It reads top-aligned like every other card here. Spreading its three
            lines down the taller row the charts set was worse than the gap it
            was meant to fill: a card whose eyebrow, value and note each sit a
            finger apart reads as three cards.

            First in the source and first on the board. */}
        {planCard}
        {phonePlanCard}
        {/* On a phone the log comes straight after the plan, ahead of the two
            charts: a deploy is what the screen is opened to read, and under two
            plots it was a screen and a half down. `lg:hidden`, so it takes no
            cell in the columns. */}
        {showsLog ? phoneLogCard : null}
        {/* The series is already a percentage of the container's own limit, so
            the ceiling is 100 and the label names what the 100 is worth. */}
        <MetricChart
          label={<>cpu {plan?.maxCPU ? <span className="text-navy-400">· limit {plan.maxCPU} vCPU</span> : null}</>}
          data={cpuGraphProps?.data}
          color="#208AEC"
          colors={cpuGraphProps?.colors}
          yMax={100}
          format={(value) => `${value.toFixed(0)}%`}
          height={OVERVIEW_PLOT_HEIGHT}
          timeWindow={containerStatsTimeWindow}
          emptyAsZero
          compact
          isLoading={isLoadingStats && !!mainServiceEnvironmentId}
          errorMessage={loadingStatsErrorMessage}
        />
        {/* The plan's own allowance is the ceiling, not the peak of the window:
            a chart that rescales to whatever the app happened to use draws an
            idle container at the top of the card. The series builder's `max`
            only stands in where there is no plan to read — and it floors at
            256MB, which would put the axis and the label at odds. */}
        <MetricChart
          label={<>ram {planRAM ? <span className="text-navy-400">· limit {planRAM}</span> : null}</>}
          data={ramGraphProps?.data}
          color="#4AC77B"
          colors={ramGraphProps?.colors}
          yMax={ramLimitBytes ?? ramGraphProps?.max}
          format={(bytes) => `${Math.round(bytes / (1024 * 1024))}`}
          unit="MB"
          height={OVERVIEW_PLOT_HEIGHT}
          timeWindow={containerStatsTimeWindow}
          emptyAsZero
          compact
          isLoading={isLoadingStats && !!mainServiceEnvironmentId}
          errorMessage={loadingStatsErrorMessage}
        />
      </div>

      {/* The security block is APP state, not deployment state, and it is shown
          ahead of everything else and outside the lastDeployment.error chain
          below. Two reasons: an app blocked by support from the review queue
          has an old successful deployment as its last one, so the deployment
          chain would render nothing at all; and a customer seeing both a
          "blocked" card and an unrelated build error at once cannot tell which
          one matters. */}
      {app.block ? (
        <SecurityBlockErrorMessage
          appId={app.id}
          block={app.block}
          userEmail={user.username}
        />
      ) : app.pendingFirstBuild && app.mainService?.mainServiceEnvironment ? (
        /* A notice with a button, not the form itself. This page is a board of
           panels; a long form dropped into the middle of it pushes everything
           real below the fold, and this is a decision made once rather than a
           permanent part of the page. Same shape as every other state reported
           here — payment pending, unverified email, security block. */
        <PendingSetupMessage
          appId={app.id}
          serviceEnvironment={app.mainService.mainServiceEnvironment}
          pendingFirstBuild={app.pendingFirstBuild}
          onReleased={async () => {
            await back4app2.releaseFirstBuild(
              app.pendingFirstBuild!.serviceEnvironmentId
            );
          }}
        />
      ) : app.status === AppStatus.PENDING_VERIFICATION ? <VerifyEmailErrorMessage userEmail={user.username} /> : (
        <>
          {isPaymentPending && mainServiceEnvironment ? (
            <PaymentPendingErrorMessage appId={app.id} mainServiceEnvironment={mainServiceEnvironment} user={user} isOwner={!!app.isOwner} onPaid={() => window.location.reload()} />
          ) : (
            <>
              {lastDeployment?.error ? 
                lastDeployment?.error instanceof DockerfileNotFoundError ? <DockerfileErrorMessage /> 
                : lastDeployment?.error instanceof KanikoImageBuildError ? <ImageBuildError errorMessage={lastDeployment.error.message} /> 
                : lastDeployment?.error instanceof DeploymentMemoryExceededError ? <OutOfMemoryError appId={app.id} /> 
                : lastDeployment?.error instanceof GitHubResourceNotFoundError ? <ResourceNotAccessibleError />
                : lastDeployment?.error instanceof CreditCardValidationRequiredError ? <CreditCardValidationRequiredErrorMessage />
                : lastDeployment?.error instanceof GitRepositoryEmptyError ? null
                : null : null}
              {/* No sleeping-container card. A sleeping deployment wakes on the
                  first request that reaches it — the balancer does it inline,
                  see `enabledRule.deploymentIsAwake` in the http-balancer — so
                  the state is transient and self-healing, and a card explaining
                  it sat on the Overview looking like a fault to be fixed. The
                  status card reads Available either way, which is what the
                  address does. */}
              {/* The log, the width of the page. It shared a row with the
                  configuration card for a while — the card a narrow column on
                  the left, the well the two columns beside it — and on a laptop
                  that left the well wrapping every other line next to ten rows
                  of short values with room to spare. The log is the one thing
                  on this screen that is as wide as it is given. */}
              {/* The box that is scrolled into view when a build starts. Its
                  scroll margin is the support bubble's: fixed bottom-right,
                  and otherwise sitting on the end of the newest line. */}
              <div ref={logCardRef} className="hidden lg:block scroll-mb-20">
              <SurfaceCard tone="surface" className="flex flex-col overflow-hidden">
                {/* Open logs belongs with the tabs: all three choose which
                    view of the log you are in. Down on the deployment line it
                    sat among facts about the deploy, next to Cancel, which is
                    an action on the deploy and not on the view. */}
                <div className="flex-none px-5 py-3 border-b border-navy-500 flex items-center justify-between gap-4">
                  <SegmentedControl
                    ariaLabel="Log source"
                    value={logTab}
                    onChange={setLogTab}
                    segments={[
                      { value: 'build', label: 'Deployment log' },
                      { value: 'runtime', label: 'Runtime log' },
                    ]}
                  />
                  {/* Each tab's "more of this" goes where more of it is. The
                      Logs screen is runtime only — a build log belongs to the
                      deploy that made it, and that is Deployments — so on the
                      build tab this link used to land somewhere that could not
                      show what you were reading. */}
                  <button
                    type="button"
                    onClick={() => navigate(`/apps/${app.id}/${logTab === 'build' ? 'deployments' : 'logs'}`)}
                    className="b4-focus flex-none rounded-control text-ds-135 font-semibold text-action-soft hover:text-white"
                  >
                    {logTab === 'build' ? 'Open deployments' : 'Open logs'}
                  </button>
                </div>
                {/* What is being read, and what can be done to it. This line
                    carried the deployment's id alone; the card above it
                    carried the id and the commit message, which is one screen
                    saying the same thing in two places. The card went, and
                    what it said comes here.

                    Two lines, because four facts on one were a sentence: what
                    was deployed on the left — the id nobody types, the branch,
                    the message a person wrote — and how it went on the right,
                    where the state, the time it took and the age of the commit
                    line up right-edged. The deploy's own clock is not here; it
                    is beside the heading, where the app's state is.

                    A fixed height, sized for those two lines. The runtime tab
                    has one line to put here, and with the line taking its
                    height from its content the card grew and shrank with the
                    tab — and the configuration under it moved every time the
                    tab changed. */}
                <div className="flex-none h-[70px] px-5 bg-navy-900 border-b border-navy-500 flex items-center justify-between gap-4">
                  {logTab === 'build' ? (
                    lastDeployment ? (<>
                      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                        <div className="flex items-center gap-3 min-w-0 font-courrier text-ds-125 text-navy-200 whitespace-nowrap">
                          {/* The whole uuid, not the first eight of it. Our
                              deployment ids are uuids and a person may need to
                              paste one into support; truncated to eight hex
                              characters it reads as a commit sha, which is a
                              different thing this screen also talks about. It
                              gives way only when the line runs out of room,
                              and then to an ellipsis rather than to the branch. */}
                          <span className="min-w-0 truncate" title={lastDeployment.id}>{lastDeployment.id}</span>
                          {/* A deployment from an image has no branch. It shows
                              the digest instead — a tag can point somewhere else
                              by the next deploy and the digest cannot. */}
                          {lastDeployment.imageDigest ? (
                            <span className="flex-none" title={lastDeployment.imageDigest}>
                              {lastDeployment.imageDigest.slice(0, 19)}…
                            </span>
                          ) : (
                            <span className="flex-none flex items-center gap-1.5">
                              {/* No branch and no digest: built from an upload. */}
                              {lastDeployment.branchName ? <BranchIcon size={14} /> : <UploadIcon size={14} />}
                              {lastDeployment.branchName || 'Upload'}
                            </span>
                          )}
                        </div>
                        <span className="text-ds-135 text-white truncate" title={lastDeployment.commitMessage || undefined}>
                          {lastDeployment.commitMessage || 'no commit message'}
                        </span>
                      </div>

                      <div className="flex-none flex flex-col items-end gap-1.5">
                        <div className="flex items-center gap-4">
                          <MyAppDeploymentStatus deployment={lastDeployment} textClassName="text-ds-135 font-medium text-white" />
                          <Timer
                            className="text-right text-ds-135 font-medium text-white"
                            realElapsedMS={(lastDeployment.deployTask && (lastDeployment.deployTask.elapsedTimeMS + (lastDeployment.deployTask.finishedAt ? 0 : new Date().getTime() - appLoadedAt.getTime()))) || 0}
                            hasFinished={(lastDeployment.deployTask && lastDeployment.deployTask.finishedAt instanceof Date) || false}
                          />
                        </div>
                        {lastDeployment.committedAt ? (
                          <span className="font-courrier text-ds-125 text-navy-200 whitespace-nowrap">
                            {timeFromNow(lastDeployment.committedAt)}
                          </span>
                        ) : null}
                      </div>
                    </>) : (
                      <span className="font-courrier text-ds-125 text-navy-200">no deployment yet</span>
                    )
                  ) : (
                    <span className="font-courrier text-ds-125 text-navy-200">container output</span>
                  )}
                  {/* The one action that belongs beside the deploy rather
                      than beside the log, and only while there is a deploy to
                      stop. */}
                  {isBuildCancelable(lastDeployment) ? (
                    <CancelBuildButton deployment={lastDeployment!} />
                  ) : null}
                </div>
                {/* A height, not a min-height: an intrinsic one here would be
                    the height of the whole log. Nothing sits beside the card
                    any more, so there is no neighbour to grow into.

                    26rem where the screen has it, and less where it does not:
                    the 18rem is everything else that has to be on screen with
                    the well — the top bar, this card's two header rows, and
                    the margin under it — so the whole card, first line to
                    last, fits a short laptop without scrolling inside the
                    scroll. Never under 12rem, which is eight lines. */}
                <div className="h-[clamp(12rem,calc(100vh-18rem),26rem)]">
                  {logTab === 'build' ? (
                    lastDeployment ? (
                      <LogBox
                        errorMessage={deploymentLogsLoadingErrorMessage}
                        logs={deploymentLogs}
                        isLoading={isLoadingDeploymentLogs}
                        canMaximize
                        fullHeight
                        hasFinished={hasFinishedBuilding(lastDeployment)}
                      />
                    ) : (
                      <div className="h-full bg-navy-950 flex items-center justify-center text-ds-135 text-navy-300">
                        Nothing built yet.
                      </div>
                    )
                  ) : (
                    mainServiceEnvironmentId ? (
                      <ContainerLogBox
                        errorMessage={containerLogsLoadingErrorMessage}
                        logs={containerLogs}
                        isLoading={isLoadingContainerLogs}
                        canMaximize
                        fullHeight
                      />
                    ) : (
                      <div className="h-full bg-navy-950 flex items-center justify-center text-ds-135 text-navy-300">
                        No container running.
                      </div>
                    )
                  )}
                </div>
              </SurfaceCard>
              </div>

              {mainServiceEnvironment ? (<>
                {/* The phone's configuration: one row to a line, each as wide
                    as the screen, and the address at the top of it — the one
                    row that leaves the dashboard instead of opening a settings
                    screen, so its trailing mark is the external-link glyph and
                    not the chevron the rest carry. From `lg` up the sidebar
                    carries the address and the grid below does not repeat it.

                    Same rule as the sidebar's link for when there is no address
                    to show: an expired domain has nothing listening, and a
                    blocked app must not advertise one at all. */}
                <SurfaceCard className="lg:hidden p-4 flex flex-col gap-3.5">
                  <Eyebrow>configuration</Eyebrow>
                  <div className="-mx-4 flex flex-col">
                    {appUrl ? (
                      <TileRow
                        dense
                        isFirst
                        tile={<LinkIcon size={22} />}
                        eyebrow="url"
                        value={appUrl}
                        title={appUrl}
                        href={`${window.location.protocol}//${appUrl}`}
                        trailing={<ExternalLinkIcon size={14} className="flex-none text-navy-300" />}
                      />
                    ) : null}
                    {configRows.map((row, i) => (
                      <TileRow
                        key={row.eyebrow}
                        dense
                        isFirst={!appUrl && i === 0}
                        tile={<row.Icon size={22} />}
                        eyebrow={row.eyebrow}
                        value={row.value}
                        onClick={row.onClick}
                        trailing={<ChevronRightIcon size={14} className="flex-none text-navy-300" />}
                      />
                    ))}
                  </div>
                </SurfaceCard>

                {/* The same rows as a grid of cells under the log: four across,
                    two below `xl`. The cells are drawn on the border colour with
                    a 1px gap, so the lines between them are the ground showing
                    through rather than a border on every cell.

                    No chevron: a cell that lights up under the pointer says it
                    can be pressed, and eight chevrons in a grid were eight
                    arrows pointing at each other. */}
                <div className="hidden lg:flex flex-col gap-3">
                  <Eyebrow>configuration</Eyebrow>
                  <div className="grid grid-cols-2 xl:grid-cols-4 gap-px bg-navy-500 border border-navy-500 rounded-card overflow-hidden">
                    {configRows.map(row => (
                      <button
                        key={row.eyebrow}
                        type="button"
                        onClick={row.onClick}
                        title={row.value}
                        className="b4-focus focus-visible:[outline-offset:-2px] min-w-0 flex items-center gap-3 px-[18px] py-3.5 bg-navy-800 text-left hover:bg-navy-700 transition-colors duration-[120ms]"
                      >
                        <span className="flex-none flex text-navy-100"><row.Icon size={22} /></span>
                        <span className="flex-1 min-w-0 flex flex-col gap-1.5">
                          <Eyebrow>{row.eyebrow}</Eyebrow>
                          <span className="text-ds-135 text-white truncate">{row.value}</span>
                        </span>
                      </button>
                    ))}
                    {/* An image app has no build and no root directory, which
                        leaves the last row short — and an empty grid cell here
                        is a block of border colour. This fills it in. */}
                    {configRows.length % 4 ? (
                      <div
                        aria-hidden
                        className={`hidden bg-navy-800 ${configRows.length % 2 ? 'lg:block' : ''} ${CONFIG_FILL[configRows.length % 4]}`}
                      />
                    ) : null}
                  </div>
                </div>
              </>) : null}
            </>
          )}
        </>
      )}
      <div className="mt-14 lg:mt-0">
        <Guides plain />
      </div>
    </div>
  </>);
};

export default MyAppOverview;
